import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { listEmployees } from '../../api';
import { getToken } from '../../auth/session';
import {
  peCreate,
  peGet,
  peId,
  peLabel,
  peList,
  peRemove,
  setSelectedPeTenantId,
} from './peClient';
import { PeNotice, PePage, PeTable, TabBar } from './PeShared';

const TABS = [
  { id: 'divisions', label: 'Divisions' },
  { id: 'users', label: 'Users' },
  { id: 'employees', label: 'Employees' },
  { id: 'calendars', label: 'Calendars' },
];

function emptyDraft(division = '') {
  return {
    userType: 'User',
    identifier: '',
    displayName: '',
    firstName: '',
    lastName: '',
    division,
    firstNameLocked: false,
    lastNameLocked: false,
  };
}

function splitName(displayName) {
  const parts = String(displayName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function suggestIdentifier(firstName, lastName) {
  const slug = [firstName, lastName]
    .map((part) => String(part || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ''))
    .filter(Boolean)
    .join('.');
  return slug || '';
}

function employeeIdentifier(row) {
  return String(row?.identifier ?? row?.Identifier ?? '').trim().toLowerCase();
}

export default function PeTenantDetail() {
  const { tenantId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.id === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'employees';
  const [tab, setTab] = useState(initialTab);
  const [tenant, setTenant] = useState(null);
  const [rows, setRows] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [roster, setRoster] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [draft, setDraft] = useState(emptyDraft());

  useEffect(() => {
    if (tenantId) setSelectedPeTenantId(tenantId);
  }, [tenantId]);

  function changeTab(next) {
    setTab(next);
    setFieldError('');
    setSearchParams(next === 'employees' ? { tab: next } : { tab: next }, { replace: true });
  }

  async function load(active = tab) {
    if (!tenantId) return;
    setBusy(true);
    setError('');
    try {
      const [detail, list, divs] = await Promise.all([
        peGet(`tenants/${tenantId}`).catch(() => ({ id: tenantId })),
        peList(pathFor(active, tenantId)),
        peList(`tenants/${tenantId}/divisions`).catch(() => []),
      ]);
      setTenant(detail && !Array.isArray(detail) ? detail : { id: tenantId });
      setRows(list);
      setDivisions(divs);
      const defaultDivision = divs[0] ? peLabel(divs[0]) : '';
      if (active === 'employees') {
        setDraft((prev) => ({
          ...prev,
          division: prev.division || defaultDivision,
        }));
      }
    } catch (err) {
      setError(err.message || 'Could not load tenant.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(tab); }, [tenantId, tab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await listEmployees(getToken());
        if (!cancelled && data?.status === 'Ok') {
          setRoster(data.employees || data.Employees || []);
        }
      } catch {
        if (!cancelled) setRoster([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function validateEmployeeDraft(nextDraft = draft) {
    const identifier = (nextDraft.identifier || '').trim();
    const firstName = (nextDraft.firstName || '').trim();
    const lastName = (nextDraft.lastName || '').trim();
    if (!identifier) return 'Identifier is required.';
    if (!firstName) return 'First name is required.';
    if (!lastName) return 'Last name is required.';
    if (divisions.length > 0 && !(nextDraft.division || '').trim()) {
      return 'Select a division for this employee.';
    }
    if (divisions.length === 0) {
      return 'Create a division first, then add the employee.';
    }
    const dup = rows.some((row) => employeeIdentifier(row) === identifier.toLowerCase());
    if (dup) return `An employee with identifier “${identifier}” already exists on this tenant.`;
    return '';
  }

  function fillIdentifierIfEmpty() {
    setDraft((prev) => {
      if ((prev.identifier || '').trim()) return prev;
      const suggested = suggestIdentifier(prev.firstName, prev.lastName);
      return suggested ? { ...prev, identifier: suggested } : prev;
    });
  }

  async function onCreate(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    setFieldError('');

    if (tab === 'employees') {
      const validation = validateEmployeeDraft();
      if (validation) {
        setFieldError(validation);
        return;
      }
    }

    setBusy(true);
    try {
      await peCreate(pathFor(tab, tenantId), payloadFor(tab, draft));
      const defaultDivision = draft.division || (divisions[0] ? peLabel(divisions[0]) : '');
      setDraft(emptyDraft(defaultDivision));
      setMessage(
        tab === 'employees'
          ? 'Employee added to Payroll Engine. No Bistro/People link is required.'
          : `${tab.slice(0, -1)} added.`,
      );
      await load(tab);
    } catch (err) {
      setError(err.message || 'Create failed.');
      setBusy(false);
    }
  }

  async function onDelete(row) {
    const id = peId(row);
    if (!id || !window.confirm(`Delete ${peLabel(row)}?`)) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await peRemove(`${pathFor(tab, tenantId)}/${id}`);
      setMessage(`${peLabel(row)} deleted.`);
      await load(tab);
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  }

  async function importFromRoster() {
    if (!roster.length) {
      setError('No Rebelity employees to import. You can still add employees natively below without Bistro.');
      return;
    }
    if (divisions.length === 0) {
      setError('Create a division first, then import or add employees.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    const existingIds = new Set(rows.map((row) => employeeIdentifier(row)).filter(Boolean));
    let created = 0;
    let skipped = 0;
    const divisionName = draft.division || peLabel(divisions[0]);
    try {
      for (const emp of roster) {
        const id = emp.employeeId || emp.EmployeeId;
        const identifier = `emp-${id}`;
        if (existingIds.has(identifier.toLowerCase())) {
          skipped += 1;
          continue;
        }
        const name = emp.displayName || emp.DisplayName || '';
        const { firstName, lastName } = splitName(name);
        const body = { identifier, firstName, lastName };
        if (divisionName) body.divisions = [divisionName];
        await peCreate(`tenants/${tenantId}/employees`, body);
        existingIds.add(identifier.toLowerCase());
        created += 1;
      }
      setMessage(`Imported ${created} employee(s)${skipped ? `, skipped ${skipped} already on PE` : ''}.`);
      await load('employees');
      changeTab('employees');
    } catch (err) {
      setError(err.message || 'Import failed.');
      setBusy(false);
    }
  }

  const employeeEmpty = tab === 'employees' && rows.length === 0;

  return (
    <PePage
      title={tenant ? peLabel(tenant) : `Tenant ${tenantId}`}
      lede="Divisions, users, employees, and calendars for this Payroll Engine tenant."
    >
      <p className="muted">
        <Link to="/app/tenants">← All tenants</Link>
      </p>
      <PeNotice error={error} busy={busy} message={message} />
      <TabBar tabs={TABS} active={tab} onChange={changeTab} />

      {tab === 'employees' ? (
        <section className="panel">
          <div className="pay-toolbar" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h2 style={{ margin: 0 }}>Add employee</h2>
              <p className="muted" style={{ margin: '8px 0 0' }}>
                Create a Payroll Engine employee directly. A BistroAdmin / People record is not required.
              </p>
            </div>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={importFromRoster}>
              Import Rebelity roster
            </button>
          </div>

          {divisions.length === 0 ? (
            <p className="error" style={{ marginTop: 12 }}>
              This tenant has no divisions yet.{' '}
              <button type="button" className="btn btn-ghost" onClick={() => changeTab('divisions')}>
                Add a division
              </button>
              {' '}before creating employees.
            </p>
          ) : null}

          {fieldError ? <p className="error" style={{ marginTop: 12 }}>{fieldError}</p> : null}

          <form className="form form-wide" style={{ marginTop: 16 }} onSubmit={onCreate}>
            <label>
              Identifier <span className="muted">(required)</span>
              <input
                value={draft.identifier || ''}
                onChange={(e) => {
                  setFieldError('');
                  setDraft({ ...draft, identifier: e.target.value });
                }}
                onBlur={fillIdentifierIfEmpty}
                required
                placeholder="jordan.lee or emp-42"
                autoComplete="off"
              />
            </label>
            <label>
              Display name
              <input
                value={draft.displayName || ''}
                onChange={(e) => {
                  setFieldError('');
                  const displayName = e.target.value;
                  const parts = splitName(displayName);
                  setDraft({
                    ...draft,
                    displayName,
                    firstName: draft.firstNameLocked ? draft.firstName : parts.firstName,
                    lastName: draft.lastNameLocked ? draft.lastName : parts.lastName,
                  });
                }}
                onBlur={fillIdentifierIfEmpty}
                placeholder="Jordan Lee"
              />
            </label>
            <label>
              First name <span className="muted">(required)</span>
              <input
                value={draft.firstName || ''}
                onChange={(e) => {
                  setFieldError('');
                  setDraft({ ...draft, firstName: e.target.value, firstNameLocked: true });
                }}
                onBlur={fillIdentifierIfEmpty}
                required
              />
            </label>
            <label>
              Last name <span className="muted">(required)</span>
              <input
                value={draft.lastName || ''}
                onChange={(e) => {
                  setFieldError('');
                  setDraft({ ...draft, lastName: e.target.value, lastNameLocked: true });
                }}
                onBlur={fillIdentifierIfEmpty}
                required
              />
            </label>
            <label>
              Division <span className="muted">(required)</span>
              <select
                value={draft.division || ''}
                onChange={(e) => {
                  setFieldError('');
                  setDraft({ ...draft, division: e.target.value });
                }}
                required={divisions.length > 0}
                disabled={divisions.length === 0}
              >
                <option value="">{divisions.length ? 'Select division' : 'No divisions yet'}</option>
                {divisions.map((div) => {
                  const name = peLabel(div);
                  return <option key={peId(div) || name} value={name}>{name}</option>;
                })}
              </select>
            </label>
            <div className="pay-toolbar" style={{ marginTop: 4 }}>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || divisions.length === 0}
              >
                Add employee
              </button>
            </div>
            <p className="muted" style={{ marginTop: 12 }}>
              Import Rebelity roster is optional — use it only when you want to copy People into this PE tenant.
              Native employees are used for case values and payrun calculation.
            </p>
          </form>
        </section>
      ) : null}

      <section className="panel">
        <div className="pay-toolbar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{TABS.find((t) => t.id === tab)?.label}</h2>
          {tab !== 'employees' ? null : (
            <span className="muted">{rows.length} employee{rows.length === 1 ? '' : 's'}</span>
          )}
        </div>
        {employeeEmpty ? (
          <p className="muted" style={{ marginTop: 12 }}>
            No PE employees yet. Use <strong>Add employee</strong> above — you do not need to import from Bistro.
          </p>
        ) : (
          <PeTable
            columns={columnsFor(tab)}
            rows={rows}
            empty="Nothing in this list yet."
            onDelete={onDelete}
          />
        )}
      </section>

      {tab !== 'employees' ? (
        <section className="panel">
          <h2>Add {tab.slice(0, -1)}</h2>
          <form className="form form-wide" onSubmit={onCreate}>
            {tab === 'divisions' ? (
              <label>
                Name
                <input value={draft.name || ''} onChange={(e) => setDraft({ name: e.target.value })} required />
              </label>
            ) : null}
            {tab === 'users' ? (
              <>
                <label>
                  Identifier
                  <input value={draft.identifier || ''} onChange={(e) => setDraft({ ...draft, identifier: e.target.value })} required placeholder="admin@tenant.local" />
                </label>
                <label>
                  First name
                  <input value={draft.firstName || ''} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
                </label>
                <label>
                  Last name
                  <input value={draft.lastName || ''} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
                </label>
                <label>
                  User type
                  <select value={draft.userType || 'User'} onChange={(e) => setDraft({ ...draft, userType: e.target.value })}>
                    <option value="User">User</option>
                    <option value="Employee">Employee</option>
                    <option value="System">System</option>
                  </select>
                </label>
              </>
            ) : null}
            {tab === 'calendars' ? (
              <label>
                Name
                <input value={draft.name || ''} onChange={(e) => setDraft({ name: e.target.value })} required />
              </label>
            ) : null}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              Add
            </button>
          </form>
        </section>
      ) : null}
    </PePage>
  );
}

function pathFor(tab, tenantId) {
  return `tenants/${tenantId}/${tab}`;
}

function payloadFor(tab, draft) {
  if (tab === 'divisions') return { name: (draft.name || '').trim() };
  if (tab === 'calendars') return { name: (draft.name || '').trim() };
  if (tab === 'users') {
    return {
      identifier: (draft.identifier || '').trim(),
      firstName: draft.firstName || '',
      lastName: draft.lastName || '',
      userType: draft.userType || 'User',
    };
  }
  const body = {
    identifier: (draft.identifier || '').trim(),
    firstName: (draft.firstName || '').trim(),
    lastName: (draft.lastName || '').trim(),
  };
  if (draft.division) body.divisions = [draft.division];
  return body;
}

function columnsFor(tab) {
  if (tab === 'employees') {
    return [
      { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
      {
        key: 'name',
        label: 'Name',
        render: (row) => peLabel(row),
      },
      {
        key: 'division',
        label: 'Division',
        render: (row) => {
          const divisions = row.divisions || row.Divisions;
          if (Array.isArray(divisions) && divisions.length) {
            return divisions.map((d) => (typeof d === 'string' ? d : peLabel(d))).join(', ');
          }
          return row.division || row.Division || '—';
        },
      },
    ];
  }
  if (tab === 'users') {
    return [
      { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
      {
        key: 'name',
        label: 'Name',
        render: (row) => peLabel(row),
      },
    ];
  }
  return [
    { key: 'name', alt: 'Name', label: 'Name' },
    { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
  ];
}
